import React, { useEffect, useState } from 'react';
import Footer from '../Header/Footer';
import Header from '../Header/Header';
import Config from '../Header/Config';
import { ToastContainer, toast } from 'react-toastify';

export default function Supplier_details() {
  const [fullName, setFullName] = useState('');
  const [contactNumber, setContactNumber] = useState('');
  const [address, setAddress] = useState('');
  const [tableData, setTableData] = useState([]);
  const [isDataChanged, setIsDataChanged] = useState(false);
  
  // Validation states
  const [errors, setErrors] = useState({
    fullName: "",
    contactNumber: "",
    address: "",
  });
  const [touched, setTouched] = useState({
    fullName: false,
    contactNumber: false,
    address: false,
  });
  const [submitAttempted, setSubmitAttempted] = useState(false);
  
  const apiUrl = Config.apiUrl;

  const capitalizeWords = (str) => {
    if (!str) return '';
    return str
      .toString()
      .split(/\s+/)
      .map((w) => (w.length > 0 ? w[0].toUpperCase() + w.slice(1).toLowerCase() : ''))
      .join(' ');
  };

  // Validation functions
  const validateFullName = (value) => {
    if (!value || value.trim() === "") {
      return "Full name is required";
    }
    if (value.trim().length < 2) {
      return "Full name must be at least 2 characters";
    }
    if (value.trim().length > 100) {
      return "Full name must not exceed 100 characters";
    }
    // Allow letters, spaces, and common name characters
    if (!/^[a-zA-Z\s\.\-']+$/.test(value.trim())) {
      return "Full name can only contain letters, spaces, dots, hyphens, and apostrophes";
    }
    return "";
  };

  const validateContactNumber = (value) => {
    if (!value || value.trim() === "") {
      return "Contact number is required";
    }
    // Remove spaces and check if it's all digits
    const digitsOnly = value.replace(/\s+/g, '');
    if (!/^\d+$/.test(digitsOnly)) {
      return "Contact number must contain only digits";
    }
    if (digitsOnly.length !== 10) {
      return "Contact number must be exactly 10 digits";
    }
    return "";
  };

  const validateAddress = (value) => {
    if (!value || value.trim() === "") {
      return "Address is required";
    }
    if (value.trim().length < 5) {
      return "Address must be at least 5 characters";
    }
    if (value.trim().length > 500) {
      return "Address must not exceed 500 characters";
    }
    return "";
  };

  // Validate all fields
  const validateAll = () => {
    const newErrors = {
      fullName: validateFullName(fullName),
      contactNumber: validateContactNumber(contactNumber),
      address: validateAddress(address),
    };
    setErrors(newErrors);
    return !newErrors.fullName && !newErrors.contactNumber && !newErrors.address;
  };

  // .......................<Unsaved Changes>.........................

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (isDataChanged) {
        const message = 'You have unsaved changes. Are you sure you want to leave?';
        event.returnValue = message;
        return message;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isDataChanged]);

  const handleHeaderNavigation = () => {
    if (isDataChanged && !window.confirm('You have unsaved changes. Are you sure you want to leave?')) {
      return false;
    }
    return true;
  };



  // .........................<Save>...........................

  const addCustomer = async (e) => {
    e.preventDefault();

    // Mark submit as attempted
    setSubmitAttempted(true);

    // Mark all fields as touched
    setTouched({
      fullName: true,
      contactNumber: true,
      address: true,
    });

    // Validate all fields
    if (!validateAll()) {
      return;
    }

    let newSupplier = {
      supplier_name: fullName.trim(),
      contact: contactNumber.replace(/\s+/g, ''), // Remove spaces from contact number
      address: address.trim(),
    };

    const isContactExists = tableData.some((existingItem) => existingItem.contact === contactNumber.replace(/\s+/g, ''));

    if (isContactExists) {
      toast.warning('Contact number already exists in the table', { autoClose: 1500 });
      setErrors((prev) => ({
        ...prev,
        contactNumber: "Contact number already exists",
      }));
      return;
    }

    try {
      let result = await fetch(`${apiUrl}/save_supplier`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(newSupplier),
      });

      if (result.ok) {
        toast.success('Supplier added successfully', { autoClose: 1500 });
        setFullName('');
        setContactNumber('');
        setAddress('');
        setErrors({
          fullName: "",
          contactNumber: "",
          address: "",
        });
        setTouched({
          fullName: false,
          contactNumber: false,
          address: false,
        });
        setSubmitAttempted(false);
        setIsDataChanged(false);
        
        // Reload the page after successful submission
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      } else {
        toast.error('Failed to add Supplier', { autoClose: 1500 });
      }
    } catch (error) {
      console.log(error);
      toast.error('Failed to add Supplier', { autoClose: 1500 });
    }
  };

  useEffect(() => {
    const fetchTableData = async () => {
      try {
        if (tableData.length === 0) {
          const response = await fetch(`${apiUrl}/get_all_supplier`);
          const data = await response.json();
          console.log(data);
          setTableData(data.data.reverse());
        }
      } catch (error) {
        console.error(error);
      }
    };

    fetchTableData();
  }, [apiUrl, tableData]);

  const cancelFields = () => {
    if (isDataChanged && !window.confirm('You have unsaved changes. Are you sure you want to cancel?')) {
      return;
    }

    setFullName('');
    setContactNumber('');
    setAddress('');
    setErrors({
      fullName: "",
      contactNumber: "",
      address: "",
    });
    setTouched({
      fullName: false,
      contactNumber: false,
      address: false,
    });
    setSubmitAttempted(false);
    setIsDataChanged(false);
  };

  const handleFullNameChange = (e) => {
    const value = capitalizeWords(e.target.value);
    setFullName(value);
    setIsDataChanged(true);
    
    // Validate on change if field has been touched or submit was attempted
    if (touched.fullName || submitAttempted) {
      setErrors((prev) => ({
        ...prev,
        fullName: validateFullName(value),
      }));
    }
  };

  const handleFullNameBlur = () => {
    setTouched((prev) => ({ ...prev, fullName: true }));
    setErrors((prev) => ({
      ...prev,
      fullName: validateFullName(fullName),
    }));
  };

  const handleContactNumberChange = (e) => {
    const value = e.target.value;
    // Allow only digits and spaces
    const digitsOnly = value.replace(/[^\d\s]/g, '');
    setContactNumber(digitsOnly);
    setIsDataChanged(true);
    
    // Validate on change if field has been touched or submit was attempted
    if (touched.contactNumber || submitAttempted) {
      setErrors((prev) => ({
        ...prev,
        contactNumber: validateContactNumber(digitsOnly),
      }));
    }
  };

  const handleContactNumberBlur = () => {
    setTouched((prev) => ({ ...prev, contactNumber: true }));
    setErrors((prev) => ({
      ...prev,
      contactNumber: validateContactNumber(contactNumber),
    }));
  };

  const handleAddressChange = (e) => {
    const value = e.target.value;
    setAddress(value);
    setIsDataChanged(true);
    
    // Validate on change if field has been touched or submit was attempted
    if (touched.address || submitAttempted) {
      setErrors((prev) => ({
        ...prev,
        address: validateAddress(value),
      }));
    }
  };

  const handleAddressBlur = () => {
    setTouched((prev) => ({ ...prev, address: true }));
    setErrors((prev) => ({
      ...prev,
      address: validateAddress(address),
    }));
  };

  return (
    <>
      <ToastContainer />
      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <Header onNavigate={handleHeaderNavigation} />
          <div className="layout-page">
            <div className="content-wrapper">
              <div className="container-xxl flex-grow-1 container-p-y">
                <h4 className="fw-bold py-3 mb-4">Supplier Details</h4>
                <div className="card mb-4">
                  <div className="card-header d-flex justify-content-between align-items-center">
                    <h5 className="mb-0 heading-heighlight">Add New Supplier</h5>
                  </div>
                  <div className="card-body">
                    <form onSubmit={addCustomer} noValidate>
                      <div className="row">
                        <div className="col-md-6 mb-3">
                          <label className="form-label" htmlFor="basic-default-fullname">
                            Full Name <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className={`form-control ${(touched.fullName && errors.fullName) || (submitAttempted && !fullName.trim()) ? 'is-invalid' : touched.fullName && !errors.fullName && fullName ? 'is-valid' : ''}`}
                            id="basic-default-fullname"
                            placeholder="Enter Full Name"
                            value={fullName}
                            onChange={handleFullNameChange}
                            onBlur={handleFullNameBlur}
                          />
                          {((touched.fullName && errors.fullName) || (submitAttempted && !fullName.trim())) && (
                            <div className="invalid-feedback d-block">
                              {errors.fullName || "Full name is required"}
                            </div>
                          )}
                          {touched.fullName && !errors.fullName && fullName && (
                            <div className="valid-feedback d-block">
                              Looks good!
                            </div>
                          )}
                        </div>
                        <div className="col-md-6 mb-3">
                          <label className="form-label" htmlFor="basic-default-contact">
                            Contact Number <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className={`form-control ${(touched.contactNumber && errors.contactNumber) || (submitAttempted && !contactNumber.trim()) ? 'is-invalid' : touched.contactNumber && !errors.contactNumber && contactNumber ? 'is-valid' : ''}`}
                            id="basic-default-contact"
                            placeholder="Enter Contact Number"
                            value={contactNumber}
                            onChange={handleContactNumberChange}
                            onBlur={handleContactNumberBlur}
                            maxLength={12}
                          />
                          {((touched.contactNumber && errors.contactNumber) || (submitAttempted && !contactNumber.trim())) && (
                            <div className="invalid-feedback d-block">
                              {errors.contactNumber || "Contact number is required"}
                            </div>
                          )}
                          {touched.contactNumber && !errors.contactNumber && contactNumber && (
                            <div className="valid-feedback d-block">
                              Looks good!
                            </div>
                          )}
                          <small className="text-muted">
                            Enter 10 digit contact number
                          </small>
                        </div>
                        <div className="col-md-6 mb-3">
                          <label className="form-label" htmlFor="basic-default-address">
                            Address <span className="text-danger">*</span>
                          </label>
                          <textarea
                            id="basic-default-address"
                            className={`form-control ${(touched.address && errors.address) || (submitAttempted && !address.trim()) ? 'is-invalid' : touched.address && !errors.address && address ? 'is-valid' : ''}`}
                            placeholder="Enter Address"
                            value={address}
                            onChange={handleAddressChange}
                            onBlur={handleAddressBlur}
                            rows={3}
                            maxLength={500}
                          />
                          {((touched.address && errors.address) || (submitAttempted && !address.trim())) && (
                            <div className="invalid-feedback d-block">
                              {errors.address || "Address is required"}
                            </div>
                          )}
                          {touched.address && !errors.address && address && (
                            <div className="valid-feedback d-block">
                              Looks good!
                            </div>
                          )}
                          <small className="text-muted">
                            {address.length}/500 characters
                          </small>
                        </div>
                        <div className="col-12">
                          <div className="col-12 d-flex justify-content-end">
                            <button
                              type="submit"
                              className="btn btn-primary"
                              style={{ marginLeft: '5px', marginRight: '5px' }}
                            >
                              Submit
                            </button>
                            <button type="button" className="btn btn-danger" onClick={cancelFields}>
                              Clear
                            </button>
                          </div>
                        </div>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
              <Footer />
              <div className="content-backdrop fade" />
            </div>
          </div>
        </div>
        <div className="layout-overlay layout-menu-toggle" />
      </div>
    </>
  );
}
